import React, { useState } from 'react';
import { submitAnswer } from '../lib/api/psychtestApi';

const QuizModal = ({ isOpen, onClose, sessionId, currentQuestion, totalQuestions, questionNumber, onSubmitAnswer }) => {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  
  if (!isOpen || !currentQuestion) return null;
  
  // Ensure options are always an array
  const ensureOptionsArray = (options) => {
    if (!options) return [];
    if (Array.isArray(options)) return options;
    if (typeof options === 'string') {
      try {
        // Try to parse if it's a JSON string
        const parsed = JSON.parse(options);
        return Array.isArray(parsed) ? parsed : [options];
      } catch (e) {
        // If parsing fails, treat as a single option
        return [options];
      }
    }
    // For objects or other types, return as a single-item array
    return [options];
  };
  
  const options = ensureOptionsArray(currentQuestion.options);
  
  const handleSubmit = async () => {
    if (selectedAnswer === null) return;
    
    await onSubmitAnswer(sessionId, currentQuestion.questionId, selectedAnswer);
    
    // Reset selected answer after submission
    setSelectedAnswer(null);
  };
  
  // Calculate progress percentage
  const progressPercentage = ((questionNumber) / totalQuestions) * 100;
  
  return (
    <div className="quiz-modal fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="quiz-modal__content bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto relative">
        {/* Decorative element */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 via-primary to-purple-400 rounded-t-xl"></div>
        
        {/* Header with gradient background */}
        <div className="quiz-modal__header px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-center justify-between">
            <h2 className="quiz-modal__title text-2xl font-bold text-gray-800">Assessment Question</h2>
            <button 
              className="quiz-modal__close-button text-gray-400 hover:text-gray-600 transition-colors"
              onClick={onClose}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="quiz-modal__body p-6">
          {/* Progress bar */}
          <div className="quiz-modal__progress mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Question {Math.min(questionNumber, totalQuestions)} of {totalQuestions}
              </span>
              <span className="text-sm font-medium text-gray-700">
                {Math.min(progressPercentage, 100).toFixed(0)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-primary h-2.5 rounded-full" 
                style={{ width: `${Math.min(progressPercentage, 100)}%` }}
              ></div>
            </div>
          </div>
          
          {/* Question text */}
          <div className="quiz-modal__question mb-8">
            <h3 className="text-xl font-medium text-gray-800 mb-2">{currentQuestion.text || currentQuestion.questionText}</h3>
            <p className="text-gray-600 text-sm">Select the option that best reflects your feelings or opinions.</p>
          </div>
          
          {/* Options - horizontal on large screens, vertical on small */}
          <div className="quiz-modal__options">
            <div className="hidden sm:flex justify-between mb-2 px-2">
              <span className="text-xs text-gray-500">Strongly Disagree</span>
              <span className="text-xs text-gray-500">Strongly Agree</span>
            </div>
            
            <div className="sm:hidden mb-2">
              <span className="text-xs text-gray-500 block text-center">Scale: Strongly Disagree (1) to Strongly Agree (5)</span>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:justify-between space-y-3 sm:space-y-0 sm:space-x-2">
              {options.map((option, index) => (
                <div 
                  key={index}
                  className={`quiz-modal__option-item flex items-center sm:flex-col sm:flex-1 p-3 rounded-lg border cursor-pointer transition-all
                    ${selectedAnswer === index 
                    ? 'border-primary bg-blue-50 shadow-sm' 
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
                  onClick={() => setSelectedAnswer(index)}
                >
                  <div className="quiz-modal__radio-wrapper mr-3 sm:mr-0 sm:mb-2">
                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center
                      ${selectedAnswer === index 
                      ? 'border-primary' 
                      : 'border-gray-300'}`}
                    >
                      {selectedAnswer === index && (
                        <div className="w-3 h-3 rounded-full bg-primary"></div>
                      )}
                    </div>
                  </div>
                  <div className="quiz-modal__option-label text-center">
                    <span className="sm:hidden">{option}</span>
                    <span className="hidden sm:block text-lg font-medium">{index + 1}</span>
                    <span className="hidden sm:block text-xs text-gray-500">{option}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="quiz-modal__footer px-6 py-4 border-t border-gray-200 flex justify-between">
          <button 
            className="quiz-modal__prev-button text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-100 border border-gray-200"
            onClick={onClose}
          >
            Save and Exit
          </button>
          <button 
            className={`quiz-modal__next-button ${selectedAnswer !== null 
              ? 'bg-primary text-white hover:bg-blue-600' 
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'} 
              px-6 py-2 rounded-lg transition-colors`}
            onClick={handleSubmit}
            disabled={selectedAnswer === null}
          >
            Next Question
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizModal; 